import React, { useState, useEffect, useRef } from 'react';

const DatePicker = ({ value, onChange, required = false, id }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(value ? new Date(value + 'T00:00:00') : null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (value) {
      // Add T00:00:00 to force local timezone interpretation
      const date = new Date(value + 'T00:00:00');
      setSelectedDate(date);
      setCurrentDate(new Date(date.getFullYear(), date.getMonth(), 1));
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateValue = (date) => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    onChange({ target: { value: formatDateValue(date) } });
    setIsOpen(false);
  };

  const handleMonthChange = (direction) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
  };

  const handleYearChange = (direction) => {
    setCurrentDate(new Date(currentDate.getFullYear() + direction, currentDate.getMonth(), 1));
  };

  const isToday = (date) => {
    const today = new Date();
    return date && 
           date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const isSelected = (date) => {
    return selectedDate &&
           date &&
           date.getDate() === selectedDate.getDate() &&
           date.getMonth() === selectedDate.getMonth() &&
           date.getFullYear() === selectedDate.getFullYear();
  };

  const days = getDaysInMonth(currentDate);
  const monthYear = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className="w-full p-3 border border-text-dark/30 rounded text-base box-border cursor-pointer focus-within:border-accent focus-within:shadow-[0_0_0_2px_rgba(186,230,253,0.25)] bg-white flex justify-between items-center"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={selectedDate ? "text-text-dark" : "text-text-dark/50"}>
          {selectedDate ? formatDate(selectedDate) : "Select date..."}
        </span>
        <span className="material-icons text-text-dark/50">
          {isOpen ? 'expand_less' : 'expand_more'}
        </span>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-20 p-4">
          {/* Header with month/year navigation */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleYearChange(-1)}
                className="p-1 hover:bg-gray-100 rounded text-text-dark/70"
              >
                <span className="material-icons text-sm">keyboard_double_arrow_left</span>
              </button>
              <button
                type="button"
                onClick={() => handleMonthChange(-1)}
                className="p-1 hover:bg-gray-100 rounded text-text-dark/70"
              >
                <span className="material-icons text-sm">chevron_left</span>
              </button>
            </div>
            
            <span className="text-text-dark font-medium text-base">{monthYear}</span>
            
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleMonthChange(1)}
                className="p-1 hover:bg-gray-100 rounded text-text-dark/70"
              >
                <span className="material-icons text-sm">chevron_right</span>
              </button>
              <button
                type="button"
                onClick={() => handleYearChange(1)}
                className="p-1 hover:bg-gray-100 rounded text-text-dark/70"
              >
                <span className="material-icons text-sm">keyboard_double_arrow_right</span>
              </button>
            </div>
          </div>

          {/* Day labels */}
          <div className="grid grid-cols-7 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-xs font-medium text-text-dark/70 p-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((date, index) => (
              <button
                key={index}
                type="button"
                onClick={() => date && handleDateSelect(date)}
                disabled={!date}
                className={`
                  p-2 text-sm rounded transition-colors duration-150
                  ${!date ? 'invisible' : ''}
                  ${isSelected(date) 
                    ? 'bg-accent text-white font-medium' 
                    : isToday(date)
                    ? 'bg-accent/10 text-accent font-medium'
                    : 'text-text-dark hover:bg-gray-100'
                  }
                `}
              >
                {date?.getDate()}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Hidden input for form validation */}
      <input
        type="hidden"
        value={formatDateValue(selectedDate)}
        required={required}
        id={id}
      />
    </div>
  );
};

export default DatePicker;